import {getUsersByIds} from "../../../../../firebase/firestore/users";
import {
    buildAgenticSectionDraftPatch,
    buildQueuedAgenticSectionPatch,
    DEFAULT_SECTION_STATE,
    ensureAgenticSectionId,
    getAgenticSectionStateFromAttrs,
    getAgenticSectionSuggestionText,
} from "./agenticSectionState";

const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authHeaders() {
    const t = localStorage.getItem('access_token');
    return t ? {'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json'} : {'Content-Type': 'application/json'};
}

function normalizeFeedbackTimestamp(createdAt) {
    if (!createdAt) return 0;
    if (typeof createdAt.toMillis === "function") return createdAt.toMillis();
    if (typeof createdAt.seconds === "number") return createdAt.seconds * 1000;

    const numericValue = Number(createdAt);
    return Number.isFinite(numericValue) ? numericValue : 0;
}

function buildUserDisplayName(user) {
    if (!user) return "Unknown user";
    return user.displayName
        || [user.firstName, user.lastName].filter(Boolean).join(" ").trim()
        || user.email
        || user.id
        || "Unknown user";
}


export function ensureSectionId(nodeView) {
    if (nodeView.node.attrs.sectionId) return nodeView.node.attrs.sectionId;

    if (typeof nodeView?.getPos === "function" && nodeView?.view?.state?.doc) {
        try {
            const pos = nodeView.getPos();
            if (typeof pos === "number") {
                const currentNode = nodeView.view.state.doc.nodeAt(pos);
                const currentSectionId = currentNode?.attrs?.sectionId;
                if (currentSectionId) return currentSectionId;
            }
        } catch (_error) {
            // Ignore stale getPos errors and fall through to generating a new id.
        }
    }

    const sectionId = ensureAgenticSectionId(nodeView.node.attrs);
    nodeView.dispatchNodeAttrs({sectionId}, false);
    return sectionId;
}

export function getCurrentSectionState(nodeView) {
    return getAgenticSectionStateFromAttrs(nodeView?.node?.attrs || DEFAULT_SECTION_STATE);
}

export function updateDraftForNode(nodeView, partialDraft = {}) {
    const currentSectionState = getCurrentSectionState(nodeView);
    nodeView.dispatchNodeAttrs(buildAgenticSectionDraftPatch(currentSectionState, partialDraft), false);
    return true;
}

export function queueGenerationForNode(nodeView, {promptText, selectedAgent} = {}) {
    const currentSectionState = getCurrentSectionState(nodeView);
    const patch = buildQueuedAgenticSectionPatch(currentSectionState, {promptText, selectedAgent});
    nodeView.dispatchNodeAttrs(patch, false);
    return {requestId: patch.requestId};
}

export async function submitFeedbackForNode(nodeView, {direction, comment} = {}) {
    const currentSectionState = getCurrentSectionState(nodeView);
    const sectionId = ensureSectionId(nodeView);

    const userId = localStorage.getItem('user_id') || null;

    await fetch(`${_awpBase}/api/documents/feedback/${sectionId}-${Date.now()}`, {
        method: 'PUT',
        headers: _authHeaders(),
        body: JSON.stringify({
            sectionId,
            requestId: currentSectionState.requestId || null,
            documentId: null,
            direction,
            comment: comment || "",
            promptText: currentSectionState.promptText || "",
            suggestionText: getAgenticSectionSuggestionText(currentSectionState),
            selectedAgent: currentSectionState.selectedAgent || DEFAULT_SECTION_STATE.selectedAgent,
            userId,
            createdAt: new Date().toISOString(),
        }),
    });
}

export function subscribeToSectionFeedback(sectionId, onChange, onError) {
    if (!sectionId) {
        Promise.resolve().then(() => onChange?.([]));
        return () => {};
    }

    let active = true;

    (async () => {
        try {
            const resp = await fetch(`${_awpBase}/api/documents/feedback?sectionId=${encodeURIComponent(sectionId)}`,
                {headers: _authHeaders()});
            const rawEntries = resp.ok ? await resp.json() : [];

            let usersById = new Map();
            try {
                const users = await getUsersByIds(rawEntries.map((entry) => entry.userId));
                usersById = new Map(users.map((user) => [user.id, buildUserDisplayName(user)]));
            } catch (error) {
                console.warn("[agenticSectionNodeViewStore] Failed to resolve feedback users", error);
            }

            if (!active) return;

            const normalizedEntries = (Array.isArray(rawEntries) ? rawEntries : [])
                .map((entry) => ({
                    id: entry.id,
                    requestId: entry.requestId || null,
                    direction: entry.direction === "down" ? "down" : "up",
                    comment: typeof entry.comment === "string" ? entry.comment.trim() : "",
                    userId: entry.userId || null,
                    userLabel: entry.userId ? (usersById.get(entry.userId) || entry.userId) : "Unknown user",
                    createdAtMillis: normalizeFeedbackTimestamp(entry.createdAt),
                }))
                .sort((left, right) => right.createdAtMillis - left.createdAtMillis);

            onChange?.(normalizedEntries);
        } catch (error) {
            console.error("[agenticSectionNodeViewStore] Failed to fetch section feedback", error);
            onError?.(error);
        }
    })();

    return () => { active = false; };
}

export function removeSectionStateForNode() {
    return true;
}

